import React from 'react';
import ClickIcon from '../assets/ClickIcon';

const ChatBubble = ({ message, isOwnMessage, onQuickReply }) => {
  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (date) => {
    const today = new Date();
    const messageDate = new Date(date);

    // Check if the message is from today
    const isToday = messageDate.toDateString() === today.toDateString();

    if (isToday) {
      return formatTime(messageDate);
    } else {
      // Show date and time for messages not from today in yyyy/mm/dd format
      const year = messageDate.getFullYear();
      const month = String(messageDate.getMonth() + 1).padStart(2, '0');
      const day = String(messageDate.getDate()).padStart(2, '0');
      const time = formatTime(messageDate);
      return `${year}/${month}/${day} ${time}`;
    }
  };

  // Function to format message content by replacing newlines with <br/> tags
  const formatMessageContent = (content) => {
    if (!content) return '';

    // Replace all occurrences of \n or \r\n with <br/>
    const formattedContent = content
      .replace(/\r\n/g, '<br/>')
      .replace(/\n/g, '<br/>')
      .replace(/\r/g, '<br/>');

    return (
      <span dangerouslySetInnerHTML={{ __html: formattedContent }} />
    );
  };

  // Function to render media preview based on media_url and media_type
  const renderMediaPreview = (message) => {
    if (!message.media_url) return null;

    const mediaType = message.message_type || '';
    const mediaUrl = message.media_url;

    // Always try to display images directly if URL looks like an image or has image media type
    if (mediaType === 'image') {
      return (
        <div className="mb-2">
          <img
            src={mediaUrl}
            alt="Media attachment"
            className="rounded h-[200px] w-[200px] cursor-pointer"
            onClick={() => window.open(mediaUrl, '_blank')}
            onError={(e) => {
              // If image fails to load, replace with download link
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = `
                <a href="${mediaUrl}" target="_blank" rel="noopener noreferrer" class="flex items-center text-blue-600 hover:underline">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download attachment
                </a>
              `;
            }}
          />
        </div>
      );
    } else if (mediaType === 'audio') {
      return (
        <div className="mb-2">
          <audio controls className="w-full">
            <source src={mediaUrl} type={mediaType} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    } else if (mediaType === 'video') {
      return (
        <div className="mb-2">
          <video controls className="w-full max-h-60 rounded">
            <source src={mediaUrl} type={mediaType} />
            Your browser does not support the video element.
          </video>
        </div>
      );
    } else if (mediaUrl) {
      // For any other media type or unknown types, show a download link
      return (
        <div className="mb-2">
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-blue-600 hover:underline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download attachment
          </a>
        </div>
      );
    }

    return null;
  };

  // Function to render quick reply buttons
  const renderQuickReplyButtons = (message) => {
    let quickReplyOptions = null;

    // Check for quick_reply type with analytics_metadata
    if (message.message_type === 'quick_reply' &&
        message.analytics_metadata?.template_metadata?.quick_reply_options) {
      quickReplyOptions = message.analytics_metadata.template_metadata.quick_reply_options;
    }
    // Fallback to direct quick_reply_options property
    else if (message.quick_reply_options && Array.isArray(message.quick_reply_options)) {
      quickReplyOptions = message.quick_reply_options;
    }

    if (!quickReplyOptions || !Array.isArray(quickReplyOptions)) return null;

    return (
      <div className="mt-2 space-y-2">
        {quickReplyOptions.map((option, index) => {
          // Handle both object format {label: {en: "text"}, value: "value"} and simple format
          const buttonText = typeof option.label === 'object'
            ? (option.label.en || option.label['zh-hk'] || Object.values(option.label)[0])
            : option.label || option.title;
          const buttonValue = option.value || option.id;

          return (
            <button
              key={index}
              onClick={() => onQuickReply && onQuickReply(buttonValue, buttonText)}
              className="block w-full text-left px-3 py-2 bg-white border border-[rgb(40,93,84)] text-[rgb(40,93,84)] rounded-lg hover:bg-[rgb(40,93,84)] hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              <div className="flex items-center justify-between">
                <span>{buttonText}</span>
                <ClickIcon className="h-4 w-4 ml-2 flex-shrink-0" />
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  // Special case for appointment messages
  if (message.is_appointment) {
    return (
      <div className="flex justify-center">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg max-w-[85%] shadow">
          <div className="flex items-center">
            <span className="mr-2">📅</span>
            <p className="text-sm text-blue-800 font-medium">{formatMessageContent(message.text || message.message_content)}</p>
          </div>
          <div className="mt-1 text-xs text-blue-600 text-right">
            <span className="bg-blue-100 px-2 py-1 rounded capitalize">{message.appointment_status}</span>
            <span className="ml-2">{formatDateTime(message.timestamp)}</span>
            {message.appointment?.has_notified &&
              <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded">
                Notified ✓
              </span>
            }
          </div>
        </div>
      </div>
    );
  }

  // Special case for event messages (notify_clinic_by_human)
  if (message.is_event) {
    return (
      <div className="flex justify-center">
        <div className="bg-green-50 border border-green-200 p-3 rounded-lg max-w-[85%] shadow">
          <div className="flex items-center">
            <span className="mr-2">📢</span>
            <p className="text-sm text-green-800 font-medium">{formatMessageContent(message.text || message.message_content)}</p>
          </div>
          <div className="mt-1 text-xs text-green-600 text-right">
            <span className="bg-green-100 px-2 py-1 rounded">
              {
                message.event_type === "notify_clinic_by_human" ? "Notified Clinic by Human" : "Notified Clinic"
              }
            </span>
            <span className="ml-2">{formatDateTime(message.timestamp)}</span>
          </div>
        </div>
      </div>
    );
  }

  // Regular message handling
  return (
    <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[80%] md:max-w-[70%] rounded-lg px-4 py-2 ${
          isOwnMessage
            ? 'bg-[rgb(40,93,84)] text-white rounded-br-none'
            : message.sender === 'system'
              ? 'bg-gray-300 text-gray-800'
              : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
        }`}
      >
        {message.sender !== 'system' && (
          <div className={`font-bold text-sm ${isOwnMessage ? 'text-[rgb(180,230,220)]' : 'text-[rgb(40,93,84)]'}`}>
            {message.sender}
          </div>
        )}
        {renderMediaPreview(message)}
        <div className="mt-1">{formatMessageContent(message.text || message.message_content)}</div>
        <div className={`text-right text-xs mt-1 ${isOwnMessage ? 'text-[rgb(180,230,220)]' : 'text-gray-500'}`}>
          {formatDateTime(message.timestamp)}
        </div>
      </div>
      {/* Quick reply buttons rendered outside the chat bubble */}
      <div className={`max-w-[80%] md:max-w-[70%]`}>
        {renderQuickReplyButtons(message)}
      </div>
    </div>
  );
};

export default ChatBubble;
